import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material'
import { EventBusy } from '@mui/icons-material'

import { AppDispatch, RootState } from '../../store'
import { fetchTodayLeaves, Leave, LeaveType, LeaveUser } from '../../store/slices/leaveSlice'
import { safeFormatDate } from '../../utils/dateFormat'
import { getInitials } from '../../utils/userColors'

const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  sick: 'Sick Leave',
  casual: 'Casual Leave',
  paid: 'Paid Leave',
  unpaid: 'Unpaid Leave',
  other: 'Other',
}

const getUser = (user: string | LeaveUser): LeaveUser | null =>
  typeof user === 'object' && user !== null ? user : null

// Same 'MMM dd, yyyy' formatting used across the Leaves feature (LeavesPage,
// ReportsPage) for consistency.
const formatDate = (value: string) => safeFormatDate(value)

const formatLeaveRange = (fromDate: string, toDate: string) =>
  fromDate === toDate ? formatDate(fromDate) : `${formatDate(fromDate)} – ${formatDate(toDate)}`

// Leave management (and this read-only view of it) is an internal/employee
// concern - clients don't see staff leave status, matching the role gating
// already applied to the Leaves feature itself (menuConfig.tsx, leaves.js).
const VISIBLE_ROLES = ['admin', 'manager', 'developer', 'tester', 'viewer']

const OnLeaveToday: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((state: RootState) => state.auth)
  const { todayLeaves, todayLoading } = useSelector((state: RootState) => state.leaves)

  const canView = !!user && VISIBLE_ROLES.includes(user.role)

  useEffect(() => {
    if (canView) {
      dispatch(fetchTodayLeaves())
    }
  }, [canView, dispatch])

  if (!canView || todayLoading || todayLeaves.length === 0) {
    return null
  }

  return (
    // Card/List structure mirrors MyDayList.tsx, its sibling section directly
    // below on the dashboard, so both read as the same kind of list.
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          On Leave Today
        </Typography>
        <List dense disablePadding>
          {todayLeaves.map((leave: Leave, index) => {
            const leaveUser = getUser(leave.user)
            if (!leaveUser) return null
            return (
              <ListItem
                key={leave._id}
                divider={index < todayLeaves.length - 1}
                sx={{ px: 0 }}
              >
                <ListItemAvatar>
                  <Avatar src={leaveUser.avatar}>
                    {!leaveUser.avatar && getInitials(leaveUser.name)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={leaveUser.name}
                  secondary={formatLeaveRange(leave.fromDate, leave.toDate)}
                />
                <Chip
                  icon={<EventBusy sx={{ fontSize: '0.8rem !important' }} />}
                  label={LEAVE_TYPE_LABELS[leave.leaveType]}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </ListItem>
            )
          })}
        </List>
      </CardContent>
    </Card>
  )
}

export default OnLeaveToday
